import React, { forwardRef, useCallback, useImperativeHandle, useMemo, useState } from "react"

import { useQueryClient } from "@tanstack/react-query"
import { addMinutes, format, isAfter } from "date-fns"
import { Route as RouteIcon } from "lucide-react"
import { toast } from "sonner"

import { PersonCombobox } from "@/components/common/PersonCombobox"
import { Badge } from "@/components/ui/badge"
import { Switch } from "@/components/ui/switch"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { withUpdateAuditComment } from "@/hooks/useShiftAuditComment"
import { type ShiftWithDisplay, useShiftDialog } from "@/hooks/useShiftDialog"
import { useShifts } from "@/hooks/useShifts"
import { getErrorMessage } from "@/lib/getErrorMessage"
import { supabase } from "@/lib/supabase"
import { ukDayEndOf, ukDayStartOf } from "@/lib/ukCalendarDate"
import { ukFormat } from "@/lib/ukFormat"
import type { UpdateShiftBody } from "@/services/shiftsService"
import { shiftsService } from "@/services/shiftsService"
import { Person } from "@/types/person"
import { exportShiftsToCSV } from "@/utils/csvExportUtils"
import { getShiftDisplayStatus } from "@/utils/shiftDisplayStatus"

import ShiftDetailsDialog from "../shift/ShiftDetailsDialog"
import { ListViewShiftSection, ShiftSubGroupRow } from "../ui/ListViewShiftSection"
import { ListViewSkeleton } from "./SkeletonLoaders"

interface ListViewProps {
  date: Date
  scheduleType: "delivery" | "collection" | "sortation" | "internal"
  filters: {
    location: string
    employer: string
    type: string
    areas: string[]
  }
  searchTerm: string
  onCountChange?: (count: number) => void
  people: Person[]
  peopleLoading: boolean
}

export interface ListViewRef {
  exportToCSV: () => void
}

const ListView = forwardRef<ListViewRef, ListViewProps>(
  (
    {
      date,
      scheduleType: _scheduleType,
      filters,
      searchTerm,
      onCountChange,
      people,
      peopleLoading,
    },
    ref,
  ) => {
    const queryClient = useQueryClient()
    const [updatingShiftId, setUpdatingShiftId] = useState<string | null>(null)
    const [expandedTimeSlots, setExpandedTimeSlots] = useState<Record<string, boolean>>({})
    const [hideEmpty, setHideEmpty] = useState(false)
    const [hideCancelled, setHideCancelled] = useState(false)

    // Use shared shift dialog hook
    const {
      shiftDetailsOpen,
      setShiftDetailsOpen,
      selectedShift,
      detailsMode,
      isSaving,
      handleShiftClick,
      handleShiftSave,
      handleForceClockIn,
      handleForceClockOut,
    } = useShiftDialog()

    // Build function request for the selected day
    const dayStart = useMemo(() => ukDayStartOf(date).toISOString(), [date])

    const dayEnd = useMemo(() => ukDayEndOf(date).toISOString(), [date])

    const { data: shifts = [], isLoading } = useShifts({
      startTime: dayStart,
      endTime: dayEnd,
      location: filters.location !== "all" ? filters.location : undefined,
      employer: filters.employer !== "all" ? filters.employer : undefined,
      type: filters.type !== "all" ? filters.type : undefined,
      areas: filters.areas.length > 0 ? filters.areas : undefined,
    })

    // People data is now passed from parent component

    const personNameById = useMemo(() => {
      const map = new Map<string, string>()
      people.forEach((person) => {
        map.set(person.id, person.person)
      })
      return map
    }, [people])

    const shiftsWithDisplay: ShiftWithDisplay[] = useMemo(() => {
      return shifts.map((s) => ({
        ...s,
        displayStatus: getShiftDisplayStatus(s),
      }))
    }, [shifts])

    // Filter shifts by search term
    const filteredShifts = useMemo(() => {
      return shiftsWithDisplay.filter((shift) => {
        const personName = shift.people?.person || "Unassigned"
        const matchesSearch =
          personName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          shift.id.includes(searchTerm)
        const shouldHideEmpty = hideEmpty && !shift.people?.person
        const shouldHideCancelled =
          hideCancelled &&
          (shift.displayStatus === "cancelled" || shift.displayStatus === "unnotified-absence")

        return matchesSearch && !shouldHideEmpty && !shouldHideCancelled
      })
    }, [shiftsWithDisplay, searchTerm, hideEmpty, hideCancelled])

    // Group shifts by area, then (when rendering) by start time
    const groupedShifts = useMemo(() => {
      const groups: { [key: string]: ShiftWithDisplay[] } = {}

      filteredShifts.forEach((shift) => {
        const area = shift.area || "Unknown"
        if (!groups[area]) {
          groups[area] = []
        }
        groups[area].push(shift)
      })

      // Sort shifts within each area group by start time, cancelled to bottom
      Object.keys(groups).forEach((area) => {
        groups[area].sort((a, b) => {
          const aCancelled = a.displayStatus === "cancelled"
          const bCancelled = b.displayStatus === "cancelled"

          if (aCancelled && !bCancelled) return 1
          if (!aCancelled && bCancelled) return -1

          const aTime = new Date(a.startTime).getTime()
          const bTime = new Date(b.startTime).getTime()

          if (isNaN(aTime) && isNaN(bTime)) return 0
          if (isNaN(aTime)) return 1
          if (isNaN(bTime)) return -1

          return aTime - bTime
        })
      })

      return groups
    }, [filteredShifts])

    // Update count for parent
    React.useEffect(() => {
      onCountChange?.(filteredShifts.length)
    }, [filteredShifts.length, onCountChange])

    const handleExportCSV = useCallback(() => {
      const filename = `shifts_daily_${format(date, "yyyy-MM-dd")}_${filteredShifts.length}.csv`
      exportShiftsToCSV(filteredShifts, filename)
    }, [date, filteredShifts])

    useImperativeHandle(
      ref,
      () => ({
        exportToCSV: handleExportCSV,
      }),
      [handleExportCSV],
    )

    const handlePersonChange = async (shiftId: string, personId: string | null) => {
      setUpdatingShiftId(shiftId)
      try {
        // Get person UUID if personId provided
        let peopleId: string | null = null
        if (personId) {
          const { data: personData, error: personError } = await supabase
            .from("people")
            .select("id")
            .eq("person", personId)
            .maybeSingle()

          if (personError) {
            toast.error("Failed to find person")
            throw personError
          }

          if (personData) {
            peopleId = personData.id
          }
        }

        // Find the shift to get its dates
        const shift = filteredShifts.find((s) => s.id === shiftId)
        if (!shift) {
          throw new Error("Block not found")
        }

        // Update shift
        const body: UpdateShiftBody = {
          id: shiftId,
          startTime: shift.startTime,
          endTime: shift.endTime,
          peopleId: peopleId,
        }
        const updateBody = await withUpdateAuditComment(body, {
          skipPrompt: shift.type !== "Internal",
        })
        await shiftsService.updateShift(updateBody)

        toast.success("Person updated successfully")

        // Invalidate queries to refresh data
        queryClient.invalidateQueries({ queryKey: ["shifts"] })
      } catch (error: unknown) {
        console.error("Failed to update person:", error)
        toast.error(getErrorMessage(error, "Failed to update person"))
      } finally {
        setUpdatingShiftId(null)
      }
    }

    const getStatusBadge = (status: ShiftWithDisplay["displayStatus"]) => {
      switch (status) {
        case "planned":
          return (
            <Badge className="bg-white border border-palette-jungleGreen200 text-palette-jungleGreen800 hover:bg-white">
              Planned
            </Badge>
          )
        case "empty":
          return <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100">Unfulfilled</Badge>
        case "open":
          return (
            <Badge className="bg-palette-jungleGreen100 text-palette-jungleGreen800 hover:bg-palette-jungleGreen100">
              Open
            </Badge>
          )
        case "in-progress":
          return (
            <Badge className="bg-green-100 text-green-800 hover:bg-green-100">In Progress</Badge>
          )
        case "clocked-in":
          return (
            <Badge className="bg-green-300 text-green-900 hover:bg-green-300">In Progress</Badge>
          )
        case "clocked-out":
          return <Badge className="bg-green-50 text-green-800 hover:bg-green-50">Completed</Badge>
        case "overdue":
          return (
            <Badge className="bg-orange-100 text-orange-800 hover:bg-orange-100">Overdue</Badge>
          )
        case "no-show":
          return <Badge className="bg-red-100 text-red-800 hover:bg-red-100">No Show</Badge>
        case "cancelled":
          return (
            <Badge className="bg-purple-100 text-purple-800 hover:bg-purple-100">Cancelled</Badge>
          )
        case "unnotified-absence":
          return (
            <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">
              Unnotified Absence
            </Badge>
          )
        default:
          return (
            <Badge className="bg-palette-jungleGreen200 text-palette-jungleGreen800 hover:bg-palette-jungleGreen200">
              {status}
            </Badge>
          )
      }
    }

    if (isLoading) {
      return <ListViewSkeleton />
    }

    const areaKeys = Object.keys(groupedShifts).sort()

    const visibilityControls = (
      <div className="flex items-center justify-between gap-6 bg-palette-jungleGreen000 border border-palette-jungleGreen200 rounded-md px-3 py-2">
        <div className="flex items-center gap-6">
          <label className="flex items-center gap-2 text-sm text-palette-jungleGreen700">
            <Switch checked={hideEmpty} onCheckedChange={setHideEmpty} />
            Hide Empty
          </label>
          <label className="flex items-center gap-2 text-sm text-palette-jungleGreen700">
            <Switch checked={hideCancelled} onCheckedChange={setHideCancelled} />
            Hide Cancelled
          </label>
        </div>
      </div>
    )

    if (areaKeys.length === 0) {
      return (
        <div className="space-y-6">
          {visibilityControls}
          <div className="text-center py-16 text-palette-jungleGreen500">
            <p className="text-lg">No blocks found for this date</p>
          </div>
        </div>
      )
    }

    const toggleTimeSlot = (area: string, slot: string) => {
      const key = `${area}__${slot}`
      setExpandedTimeSlots((prev) => ({
        ...prev,
        [key]: !prev[key],
      }))
    }

    return (
      <div className="space-y-6">
        {visibilityControls}
        {areaKeys.map((area) => {
          const areaShifts = groupedShifts[area]

          // Build sub-groups by start time within this area
          const timeGroups: Record<string, ShiftWithDisplay[]> = {}
          areaShifts.forEach((shift) => {
            if (!shift.startTime || isNaN(new Date(shift.startTime).getTime())) {
              const key = "No start time"
              if (!timeGroups[key]) timeGroups[key] = []
              timeGroups[key].push(shift)
              return
            }
            const key = ukFormat(shift.startTime, "HH:mm")
            if (!timeGroups[key]) timeGroups[key] = []
            timeGroups[key].push(shift)
          })

          const timeSlots = Object.keys(timeGroups).sort((a, b) => {
            if (a === "No start time") return 1
            if (b === "No start time") return -1
            return a.localeCompare(b)
          })

          return (
            <ListViewShiftSection
              key={area}
              title={area}
              count={areaShifts.length}
              meta={format(date, "EEEE, MMMM d, yyyy")}
            >
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[200px]">People</TableHead>
                    <TableHead className="w-[100px]">Start Time</TableHead>
                    <TableHead className="w-[100px]">End Time</TableHead>
                    <TableHead className="w-[150px]">Block Area</TableHead>
                    <TableHead className="w-[100px]">Type</TableHead>
                    <TableHead className="w-[150px]">Block Location</TableHead>
                    <TableHead className="w-[180px]">Preassigned Route</TableHead>
                    <TableHead className="w-[200px]">Notes</TableHead>
                    <TableHead className="w-[120px]">Status</TableHead>
                    <TableHead className="w-[150px]">Updated at</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {timeSlots.map((slot) => {
                    const slotKey = `${area}__${slot}`
                    const isSlotExpanded = expandedTimeSlots[slotKey] === true // default collapsed
                    return (
                      <React.Fragment key={slot}>
                        <ShiftSubGroupRow
                          label={`Start ${slot}`}
                          count={timeGroups[slot].length}
                          expanded={isSlotExpanded}
                          onToggle={() => toggleTimeSlot(area, slot)}
                          colSpan={10}
                        />
                        {isSlotExpanded &&
                          timeGroups[slot].map((shift) => (
                            <TableRow
                              key={shift.id}
                              className="cursor-pointer hover:bg-palette-jungleGreen000"
                              onClick={() => handleShiftClick(shift)}
                            >
                              <TableCell onClick={(e) => e.stopPropagation()}>
                                {(() => {
                                  const now = new Date()
                                  const shiftStart = new Date(shift.startTime)
                                  const isUnfulfilledAndLate =
                                    shift.displayStatus === "empty" &&
                                    isAfter(now, addMinutes(shiftStart, 10))

                                  const canEdit =
                                    (shift.displayStatus === "planned" ||
                                      shift.displayStatus === "empty") &&
                                    !isUnfulfilledAndLate

                                  const primaryPersonId = shift.people?.primaryPersonId ?? null
                                  const primaryDriverName = primaryPersonId
                                    ? (personNameById.get(primaryPersonId) ?? null)
                                    : null

                                  return (
                                    <div className="flex flex-col">
                                      {canEdit ? (
                                        <PersonCombobox
                                          value={shift.people?.person || undefined}
                                          onValueChange={(personId) =>
                                            handlePersonChange(shift.id, personId)
                                          }
                                          disabled={updatingShiftId === shift.id}
                                          people={people}
                                          isLoading={peopleLoading}
                                          area={shift.area}
                                          sheriffType={shift.type}
                                          employer={shift.employer}
                                          location={shift.location}
                                        />
                                      ) : (
                                        <span className="text-sm text-palette-jungleGreen700">
                                          {shift.people?.person || "Unassigned"}
                                        </span>
                                      )}
                                      {primaryPersonId && (
                                        <span className="text-xs text-palette-jungleGreen500 mt-0.5">
                                          Primary: {primaryDriverName ?? "Unknown"}
                                        </span>
                                      )}
                                    </div>
                                  )
                                })()}
                              </TableCell>
                              <TableCell>
                                {shift.startTime && !isNaN(new Date(shift.startTime).getTime())
                                  ? ukFormat(shift.startTime, "HH:mm")
                                  : "-"}
                              </TableCell>
                              <TableCell>
                                {shift.endTime && !isNaN(new Date(shift.endTime).getTime())
                                  ? ukFormat(shift.endTime, "HH:mm")
                                  : "-"}
                              </TableCell>
                              <TableCell>{shift.area}</TableCell>
                              <TableCell>{shift.type}</TableCell>
                              <TableCell>{shift.location}</TableCell>
                              <TableCell>
                                {shift.preassignedRoute ? (
                                  <Badge
                                    variant="outline"
                                    className="bg-blue-100 text-blue-900 border-blue-300 text-xs w-fit"
                                  >
                                    <RouteIcon className="h-3.5 w-3.5" />
                                    <span className="ml-1 truncate">{shift.preassignedRoute}</span>
                                  </Badge>
                                ) : (
                                  "-"
                                )}
                              </TableCell>
                              <TableCell>
                                {shift.notes ? (
                                  <Tooltip delayDuration={300}>
                                    <TooltipTrigger asChild>
                                      <div className="max-w-[200px] truncate cursor-help">
                                        {shift.notes}
                                      </div>
                                    </TooltipTrigger>
                                    <TooltipContent className="max-w-sm whitespace-pre-wrap break-words">
                                      {shift.notes}
                                    </TooltipContent>
                                  </Tooltip>
                                ) : (
                                  "-"
                                )}
                              </TableCell>
                              <TableCell>{getStatusBadge(shift.displayStatus)}</TableCell>
                              <TableCell>
                                {shift.cancelledTime &&
                                !isNaN(new Date(shift.cancelledTime).getTime())
                                  ? ukFormat(shift.cancelledTime, "MMM d, yyyy h:mm a")
                                  : "-"}
                              </TableCell>
                            </TableRow>
                          ))}
                      </React.Fragment>
                    )
                  })}
                </TableBody>
              </Table>
            </ListViewShiftSection>
          )
        })}

        {/* Shift Details Dialog */}
        <ShiftDetailsDialog
          mode={detailsMode}
          shift={selectedShift}
          open={shiftDetailsOpen}
          onOpenChange={setShiftDetailsOpen}
          onSave={handleShiftSave}
          onForceClockIn={handleForceClockIn}
          onForceClockOut={handleForceClockOut}
          isSaving={isSaving}
          people={people}
          peopleLoading={peopleLoading}
        />
      </div>
    )
  },
)

export default ListView
